import { api } from '../api';

// ─── Shared helpers ───────────────────────────────────────────────────────────

function buildQs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MenuItem {
  id: number;
  menuId: number;
  name: string;
  description: string | null;
  category: string | null;
  station: string | null;
  price: number | null;
  prepTimeMinutes: number;
  imageUrl: string | null;
  isFeatured: boolean;
  /** The recipes that make up this menu item, with their quantities. */
  recipes: Array<{ recipeId: number; name: string; quantity: number }>;
  allergens: string[];
}

export interface DiningTable {
  id: number;
  section: string;
  name: string;
  seatCount: number;
}

export interface OrderItemInput {
  menuItemId: number;
  qty: number;
  notes?: string;
}

export type ServiceType = 'DINE_IN' | 'TAKEOUT' | 'DELIVERY';
export type DeckPriority = 'HIGH' | 'NORMAL' | 'LOW';

// ─── Board list types ─────────────────────────────────────────────────────────

export type BoardSessionStatus = 'ON_DECK' | 'COOKING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

export interface BoardSessionSummary {
  id: number;
  status: BoardSessionStatus;
  chefName: string | null;
  elapsedMinutes: number;
  elapsedSeconds: number;
  targetMinutes: number;
  isLate: boolean;
  completedAt: string | null;
}

export interface BoardOrder {
  id: number;
  /** Formatted ticket number, e.g. "#001" */
  tkt: string;
  /** Primary item name */
  title: string;
  /** All item names in this order */
  allItems: string[];
  status: 'SENT' | 'COMPLETED';
  serviceType: ServiceType;
  priority: DeckPriority;
  guestName: string | null;
  guestCount: number | null;
  phone: string | null;
  allergens: string[];
  table: { id: number; section: string; name: string } | null;
  /** Lead chef name, or first active session's chef */
  chefName: string | null;
  sessions: BoardSessionSummary[];
  /** Max elapsed across active sessions */
  elapsedMinutes: number;
  /** The same, in seconds — the board runs its own clock off this. */
  elapsedSeconds: number;
  /** Target minutes for the leading session */
  targetMinutes: number;
  /** elapsed / target clamped 0-100 */
  progressPct: number;
  /** true when any session is still ON_DECK / COOKING / PAUSED */
  isActive: boolean;
  /** true when cooking is done but order hasn't been passed through the expo window */
  isPlating: boolean;
  isLate: boolean;
  /** true when all active sessions are PAUSED */
  isPaused: boolean;
  /** First item's recipe image, if available */
  imageUrl: string | null;
  sentAt: string | null;
  passedAt: string | null;
  createdAt: string;
  /**
   * Sum of the prices snapshotted when the order was sent, or null when a line
   * has no price at all — which is not the same thing as costing nothing
   * (T-901).
   */
  totalPrice: number | null;
}

export interface ListOrdersParams {
  locationId?: number;
  /** ISO date string; API defaults to start of today UTC */
  dateFrom?: string;
}

export interface CreateOrderPayload {
  locationId: number;
  menuId?: number;
  tableId?: number;
  serviceType: ServiceType;
  items: OrderItemInput[];
  guestName?: string;
  phone?: string;
  guestCount?: number;
  serverId?: number;
  leadChefId?: number;
  allergens?: string[];
  reservationTag?: string;
  kitchenNotes?: string;
  priority?: DeckPriority;
  targetMinutes?: number;
  printChit?: boolean;
  notifyAllergen?: boolean;
}

export interface Order {
  id: number;
  organizationId: number;
  locationId: number;
  serviceType: ServiceType;
  status: 'DRAFT' | 'SENT' | 'CANCELLED';
  guestName: string | null;
  guestCount: number | null;
  priority: DeckPriority;
  targetMinutes: number | null;
  sentAt: string | null;
  createdAt: string;
  items: Array<{
    id: number;
    menuItemId: number;
    qty: number;
    unitPrice: number;
    menuItem: { id: number; displayName: string | null; price: number | null; recipe: { name: string } };
  }>;
  cookingSessions: Array<{ id: number; recipeId: number; status: string }>;
  table: { id: number; section: string; name: string } | null;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const ordersApi = {
  getMenuItems: (params?: { menuId?: number; category?: string; search?: string }) => {
    const sp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) sp.append(k, String(v));
      });
    }
    const qs = sp.toString();
    return api.get<MenuItem[]>(`/orders/menu-items${qs ? `?${qs}` : ''}`);
  },

  getMenuCategories: (params?: { menuId?: number }) => {
    const sp = new URLSearchParams();
    if (params?.menuId) sp.append('menuId', String(params.menuId));
    const qs = sp.toString();
    return api.get<string[]>(`/orders/menu-categories${qs ? `?${qs}` : ''}`);
  },

  getDiningTables: (params?: { locationId?: number; section?: string }) => {
    const sp = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) sp.append(k, String(v));
      });
    }
    const qs = sp.toString();
    return api.get<DiningTable[]>(`/orders/dining-tables${qs ? `?${qs}` : ''}`);
  },

  list: (params?: ListOrdersParams) =>
    api.get<BoardOrder[]>(`/orders${buildQs({ ...(params ?? {}) })}`),

  /** Mark an order as passed through the expo window (Plating → next stage) */
  pass: (id: number) =>
    api.patch<{ id: number; passedAt: string }>(`/orders/${id}/pass`, {}),

  /** Mark an order as handed off / fulfilled — removes it from the board permanently */
  complete: (id: number) =>
    api.patch<{ id: number; status: string }>(`/orders/${id}/complete`, {}),

  create: (data: CreateOrderPayload) => api.post<Order>('/orders', data),

  getById: (id: number) => api.get<Order>(`/orders/${id}`),
};
